/* eslint-disable camelcase */
import { useEffect, useState } from 'react';

import {
  fetchCultureSelectorData,
  persistUserSessionData,
  useCultureSelectorData,
} from '@skyscanner-internal/global-components/culture-selector/helpers';
import GlobalHeader from '@skyscanner-internal/global-components/header';
import { loginModalInit } from '@skyscanner-internal/global-components/header/helpers';
import { SOURCES } from '@skyscanner-internal/global-components/login-form/constants/tracking';
import { SubscriptionClient } from '@skyscanner-internal/global-components/login-form/helpers';
import { logError, logOperationalEvent, logWarn } from 'saddlebag-logger';
import observer from 'saddlebag-observer';
import { getProfilePicture, isLoggedIn } from 'saddlebag-user-management';

import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { loadClient } from './loadClient';

import type { Configs } from '@shared/types/configs';
import type { HeaderProps } from '@shared/types/global-components/header-props';
import type { UserContext } from '@shared/types/user-context';
import type { Optional } from '@shared/types/utils';
import type { SubscriptionClientInterface } from '@skyscanner-internal/global-components/google-one-tap';
import type { PartialAuthClient } from '@skyscanner-internal/global-components/login-form/types/auth-client';

import STYLES from './Header.module.scss';

import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import { isBrowser } from '@shared/utils-universal/is-browser';

const cls = cssModules(STYLES);

type Props = HeaderProps & {
  userContext: UserContext;
  configs: Configs;
  isProductionEnv: boolean;
};

const Header = ({
  configs,
  cultureSelectorProps,
  isProductionEnv,
  locale,
  market,
  userContext,
  v11__LoginFormProps,
  ...rest
}: Props) => {
  const {
    currencies,
    enMarkets,
    isFetchingCultureSelectorData,
    locales,
    markets,
    onCultureSelectorButtonClick,
  } = useCultureSelectorData(fetchCultureSelectorData, locale, false);
  const [isLoginFormOpen, setIsLoginFormOpen] = useState(false);
  const [authClient, setAuthClient] =
    useState<Optional<PartialAuthClient>>(undefined);
  const [subscriptionClient, setSubscriptionClient] =
    useState<Optional<SubscriptionClientInterface>>(undefined);
  const [loginSource, setLoginSource] = useState(v11__LoginFormProps.source);

  useEffect(() => {
    // Listen for S2L login events to dynamically set source
    // Subscribe BEFORE loginModalInit to ensure this runs first
    const handleS2LLogin = (eventData: any) => {
      if (eventData?.copyType === 'S2L_SAVE') {
        setLoginSource(SOURCES.S2L_ITINERARY);
      }
    };
    const subscriptionHandle = observer.subscribe(
      'sid-login-button-clicked',
      handleS2LLogin,
    );
    loginModalInit({
      setLoginFormVisible: setIsLoginFormOpen,
    });
    return () => {
      observer.unsubscribe(subscriptionHandle);
    };
  }, []);

  const PACKAGES_HEADER_TAB_VISUAL_CHANGE_VERSIONS = [
    'VISUAL_CHANGE_ONLY',
    'VISUAL_CHANGE_AND_NEW_BUBBLE',
  ];

  const PACKAGES_HEADER_TAB_NEW_BUBBLE_VERSIONS = [
    'NO_VISUAL_CHANGES_WITH_NEW_BUBBLE',
    'VISUAL_CHANGE_AND_NEW_BUBBLE',
  ];

  const { packagesHeaderTabEnabled } = configs;

  const headerProps = {
    ...rest,
    market,
    locale,
    showPackagesTab: packagesHeaderTabEnabled,
    unstable__packagesEnableVisualChange:
      PACKAGES_HEADER_TAB_VISUAL_CHANGE_VERSIONS.includes(
        configs.packagesHeaderTabVersion,
      ) && packagesHeaderTabEnabled,
    unstable__newBubble:
      PACKAGES_HEADER_TAB_NEW_BUBBLE_VERSIONS.includes(
        configs.packagesHeaderTabVersion,
      ) && packagesHeaderTabEnabled,
    cultureSelectorProps: {
      ...cultureSelectorProps,
      markets,
      locales,
      currencies,
      enMarkets,
      isProductionEnv,
      persistUserSessionDataFn:
        configs.enableAnonymousUtidPropagationOnCultureSelectorTldRedirect
          ? persistUserSessionData
          : undefined,
    },
  };

  useEffect(() => {
    if (!authClient) {
      loadClient('AuthClient').then((AuthClient) => {
        setAuthClient(
          new AuthClient({
            authConsumer: 'hotels-website',
            env: isProductionEnv ? 'production' : 'development',
            locale,
            market,
            strings: v11__LoginFormProps.authClient.strings,
          }),
        );
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!subscriptionClient) {
      const newSubscriptionClient = new SubscriptionClient();
      setSubscriptionClient(newSubscriptionClient);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      id="global-header-container"
      className={cls(`Header__${userContext.pageType}`)}
      data-testid="global-header"
    >
      <GlobalHeader
        {...headerProps}
        isFetchingCultureSelectorData={isFetchingCultureSelectorData}
        onCultureSelectorButtonClick={onCultureSelectorButtonClick}
        logger={{
          logOperationalEvent,
          logError,
          logWarn,
        }}
        userLoggedIn={
          isBrowser()
            ? isLoggedIn()
            : Promise.resolve(userContext.userPreferences.isLoggedIn)
        }
        getProfilePicture={getProfilePicture}
        isProductionEnv={isProductionEnv}
        loginFormProps={
          authClient && subscriptionClient
            ? {
                ...v11__LoginFormProps,
                source: loginSource,
                consumer: 'hotels-website',
                isProductionEnv,
                authClient,
                isOpen: isLoginFormOpen,
                subscriptionClient,
                onVisibilityChange: ({ shouldBeOpen }) => {
                  setIsLoginFormOpen(!!shouldBeOpen);
                  if (!shouldBeOpen) {
                    setLoginSource(SOURCES.HEADER);
                  }
                },
              }
            : undefined
        }
      />
    </div>
  );
};

export default withConfiguration(withUserContext(Header));
